import { isNil } from 'lodash';
import React, { useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { MBBottomSheet, MBBottomSheetTitleBar } from '@shared/components/bottomSheet';
import { ShareQRCode } from '@shared/components/qrCodes';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { usePermission } from '@shared/util/permissions/hooks';
import { useCardDefinition } from '../../hooks';
import {
  copyToClipboard,
  useHandleCopyQr,
  useSharedCardLinkState,
} from '../hooks/ShareQrCodeOperations';
import styles from './ShareSheet.module.css';

interface Props {
  anchorName?: string;
  card?: { id: MBAPI.Scalar.UUID; workflowTemplateId: MBAPI.Scalar.UUID | null };
  onCloseSheet(): void;
}

export function ShareSheet({ anchorName, card, onCloseSheet }: Props) {
  const channel = useContext(CardChannelContext);
  const sharedCardLinkPermissions = usePermission({
    action: 'manage',
    subject: 'SharedCardLink',
    channelId: channel?.id,
  });
  const colors = useContext(ColorThemeContext);
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const nocodeReadonlyPublicTemplate = cardDefinition?.templates?.find(uiTemplate => {
    return uiTemplate.template.context === 'nocode-public-readonly';
  });

  const privateCardUrl = useMemo(() => {
    return `${import.meta.env.VITE_APP_URL}/${channel?.slug}/cards/${card?.id}`;
  }, [channel?.slug, card?.id]);

  const { showPublicQrCode, publicLink, onToggleShowPublicQrCode } = useSharedCardLinkState({
    channelId: channel?.id,
    type: 'VIEW_CARD',
    viewTemplateId: nocodeReadonlyPublicTemplate?.template?.slug,
    cardId: card?.id,
    disabled: sharedCardLinkPermissions.enabled,
  });

  const copyQr = useHandleCopyQr();

  const renderTitleBar = () => {
    return (
      <MBBottomSheetTitleBar
        leftIcon="arrow-point-left"
        leftIconColor={colors.foregroundActive}
        onLeftIconPress={onCloseSheet}
        title={i18n('qrCode.share.shareCardTitle')}
      />
    );
  };

  const isLowCodeCard = isNil(card?.workflowTemplateId);
  const hasNoCodeReadonlyPublicTemplate =
    !isNil(card?.workflowTemplateId) && !isNil(nocodeReadonlyPublicTemplate);

  return (
    <MBBottomSheet
      anchorName={anchorName}
      initialSize="full"
      renderTitleBar={renderTitleBar}
      onClose={onCloseSheet}
      className={styles.shareSheet}
    >
      <View paddingBottom={1}>
        <ShareQRCode
          privateShare={{
            url: privateCardUrl,
            onCopyQr: copyQr,
            onCopyLink: copyToClipboard,
          }}
          canCreatePublicCode={
            sharedCardLinkPermissions.enabled && (isLowCodeCard || hasNoCodeReadonlyPublicTemplate)
          }
          hasPublicQrCode={showPublicQrCode}
          publicUrl={publicLink?.url}
          onSwitchPublic={onToggleShowPublicQrCode}
          onCopyPublicQr={copyQr}
          onCopyPublicLink={copyToClipboard}
        />
      </View>
    </MBBottomSheet>
  );
}
